import React, { useEffect, useState } from "react";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import ReactPaginate from "react-paginate";
import SideBar from "./SideBar";

const API_URL = process.env.REACT_APP_API_URL;

const LeaveRequestsRM = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const fetchLeaveRequests = async (email) => {
    try {
      const token = await localforage.getItem("token");
      const response = await api.get(`${API_URL}/leavedata/leave-requests-emails`, {
        params: { receiverEmail: email },
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedRequests = response.data.leaveRequests.map((request) => ({
        ...request,
        leaveStatus: request.leaveStatus || "pending",
      }));
      setLeaveRequests(updatedRequests);
      setFilteredRequests(updatedRequests);
    } catch (err) {
      setError("Error fetching leave requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getReceiverEmail = async () => {
      try {
        const email = await localforage.getItem("email");
        if (email) fetchLeaveRequests(email);
        else setError("Receiver email not found.");
      } catch (err) {
        setError("Error retrieving email");
      }
    };
    getReceiverEmail();
  }, []);

  const updateLeaveStatus = async (id, leaveStatus, leaveType, startDate, endDate) => {
    try {
      const token = await localforage.getItem("token");
      await api.put(`${API_URL}/leavedata/update-leave-status/${id}`, 
        { leaveStatus, leaveType, startDate, endDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeaveRequests((prev) => prev.map((r) => r._id === id ? { ...r, leaveStatus } : r));
      setFilteredRequests((prev) => prev.map((r) => r._id === id ? { ...r, leaveStatus } : r));
    } catch (err) {
      setError(err.response?.data?.error || "Error updating leave status");
    }
  };

  useEffect(() => {
    const filtered = searchQuery
      ? leaveRequests.filter((r) =>
          r.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.name?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : leaveRequests;
    setFilteredRequests(filtered);
    setCurrentPage(0);
  }, [searchQuery, leaveRequests]);

  const pageCount = Math.ceil(filteredRequests.length / itemsPerPage);
  const displayedRequests = filteredRequests.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  return (
    <>
      <SideBar />
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">Leave Request Approvals</h1>
          <p className="dashboard-subtitle">Review and approve team leave requests</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {loading ? (
          <div className="spinner"></div>
        ) : (
          <div className="data-table-container">
            <div className="data-table-header">
              <h3 className="data-table-title">Pending Approvals</h3>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="data-table-search"
              />
            </div>
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Leave Type</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRequests.map((leave) => (
                    <tr key={leave._id}>
                      <td>{leave.name}</td>
                      <td>{leave.email}</td>
                      <td>{leave.leaveType.replace(/([A-Z])/g, ' $1').trim()}</td>
                      <td>{new Date(leave.startDate).toLocaleDateString("en-GB")}</td>
                      <td>{new Date(leave.endDate).toLocaleDateString("en-GB")}</td>
                      <td style={{ minWidth: "200px" }}>{leave.reasonForLeave}</td>
                      <td>
                        <span className={`status-badge status-badge-${
                          leave.leaveStatus === "approved" ? "success" :
                          leave.leaveStatus === "not approved" ? "rejected" : "pending"
                        }`}>
                          {leave.leaveStatus.charAt(0).toUpperCase() + leave.leaveStatus.slice(1)}
                        </span>
                      </td>
                      <td>
                        <button
                          className="table-action-btn table-action-btn-view"
                          onClick={() => updateLeaveStatus(leave._id, "approved", leave.leaveType, leave.startDate, leave.endDate)}
                          disabled={leave.leaveStatus !== "pending"}
                        >
                          Approve
                        </button>
                        <button
                          className="table-action-btn table-action-btn-delete"
                          onClick={() => updateLeaveStatus(leave._id, "not approved", leave.leaveType, leave.startDate, leave.endDate)}
                          disabled={leave.leaveStatus !== "pending"}
                        >
                          Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pageCount > 1 && (
              <div className="pagination">
                <ReactPaginate
                  previousLabel={"← Previous"}
                  nextLabel={"Next →"}
                  pageCount={pageCount}
                  onPageChange={(data) => setCurrentPage(data.selected)}
                  containerClassName={"pagination"}
                  pageClassName={"pagination-btn"}
                  activeClassName={"active"}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default LeaveRequestsRM;
